import { AppInstallOption } from '@wix/editor-platform-host-integration-apis';
import { InstallInitiator } from '@wix/editor-platform-sdk-types';
import { findLast } from 'lodash';
import { reportBi } from '../../../../essentials';
import { PlatformContext } from '../../../../types/platformApi';
import { getGUID } from '../../../utils/guid';
import { BI_EVENT_IDS } from './constants';
import { UnifiedComponentsError } from './errors';
import { getArtifactVersion, getEditorSessionId } from '../../../../utils';

type ExtraDataForBIsMap = {
  NAVIGATE_TO_DEV_CENTER: { appDefinitionId: string };
  NEW_COMPONENTS_INSTALL: {
    appDefinitionId: string;
    components_list: any[];
    numberInstalledComponents: number;
  };
  UNIFIED_APP_INSTALL: { appDefinitionId: string };
  INSTALL_CODE_PACKAGES: {
    appDefinitionId: string;
  };
  PLATFORM_RUN_SCRIPTS: {
    appDefinitionId: string;
  };
  PLATFORM_ERROR: {
    errorCode: string;
    errorData: string;
    errorDetails: string;
    errorMessage: string;
    errorType: string;
  };
};

type ExtraDataForEvent<T extends keyof typeof BI_EVENT_IDS> =
  T extends keyof ExtraDataForBIsMap ? ExtraDataForBIsMap[T] : {};

type WithBI = <T extends keyof typeof BI_EVENT_IDS, V>(
  biEvent: T,
  extra: ExtraDataForEvent<T>,
  func: () => Promise<V>,
) => Promise<V>;

type ReportBI = <T extends keyof typeof BI_EVENT_IDS>(
  biEvent: T,
  extra: ExtraDataForEvent<T>,
) => void;

export type BIReporter = {
  withBI: WithBI;
  reportBI: ReportBI;
};

export function getBIReporter(
  context: PlatformContext,
  appDefIds: string[],
  options?: Record<string, AppInstallOption>,
): BIReporter {
  const installationId = getGUID();
  const editorType = context.platformApiMethods.editor.info.getHostType();
  const fallbackAppDefIdForOrigin = findLast(
    appDefIds,
    (appDefId: string) => options?.[appDefId]?.origin,
  );
  const esi = getEditorSessionId();
  const dmVersion = getArtifactVersion('document-management');

  let provisionDuration = 0;

  const getOriginInfo = (appDefinitionId?: string): string => {
    if (appDefinitionId) {
      const appOptions = options?.[appDefinitionId];
      if (appOptions) {
        const originByOptions =
          appOptions.origin?.info?.type ||
          appOptions.origin?.info?.appDefinitionId;

        if (originByOptions) {
          return originByOptions;
        }
      }
    }

    if (
      fallbackAppDefIdForOrigin &&
      appDefinitionId !== fallbackAppDefIdForOrigin
    ) {
      return getOriginInfo(fallbackAppDefIdForOrigin as string);
    }

    return '';
  };

  const isDependencyApp = (appDefinitionId: string): boolean => {
    const appOptions = options?.[appDefinitionId];
    if (!appOptions?.origin?.initiator) {
      return false;
    }

    return [InstallInitiator.Dependency_Service, InstallInitiator.App].includes(
      appOptions.origin.initiator,
    );
  };

  const getAppDefinitionIdFromExtra = (extra?: any) =>
    Array.isArray(extra?.appDefinitionId)
      ? extra?.appDefinitionId[extra?.appDefinitionId.length - 1]
      : extra?.appDefinitionId;

  const reportProcess = <V>(
    evid: number,
    func: () => Promise<V>,
    extra?: any,
  ) => {
    const appDefinitionId = getAppDefinitionIdFromExtra(extra);

    const extendedExtra = {
      ...extra,
      ...context.intentInstallServices.getBIExtras({
        appDefinitionId: getAppDefinitionIdFromExtra(extra),
      }),
    };

    const startTime = Date.now();
    let endStatus = 'end';
    const origin = getOriginInfo(appDefinitionId);

    reportEvent(evid, {
      duration: 0,
      type: 'start',
      origin,
      ...extendedExtra,
    });
    return new Promise<V>((resolve, reject) => {
      func()
        .then(resolve)
        .catch((error) => {
          reportEvent(
            BI_EVENT_IDS.PLATFORM_ERROR,
            (error as UnifiedComponentsError)?.getExtraDetailsForBI?.(),
          );
          reject(error);
          endStatus = 'error';
        })
        .finally(() => {
          const duration = Date.now() - startTime;

          if (evid === BI_EVENT_IDS.PROVISION_APPS) {
            provisionDuration = duration;
          }

          reportEvent(evid, {
            duration,
            type: endStatus,
            origin,
            ...extendedExtra,
            ...(evid === BI_EVENT_IDS.UNIFIED_APP_INSTALL
              ? {
                  provision_duration: provisionDuration,
                  install_duration: duration,
                  total_duration: provisionDuration + duration,
                }
              : {}),
          });
        });
    });
  };

  const reportEvent = (evid: number, extra?: any) => {
    const extendedExtra = {
      ...extra,
      ...context.intentInstallServices.getBIExtras({
        appDefinitionId: getAppDefinitionIdFromExtra(extra),
      }),
    };

    reportBi(evid, {
      installationId,
      editorType,
      ...extendedExtra,
    });
  };

  return {
    withBI: <T extends keyof typeof BI_EVENT_IDS, V>(
      biEvent: T,
      extra: ExtraDataForEvent<T>,
      func: () => Promise<V>,
    ): Promise<V> => {
      const evid = BI_EVENT_IDS[biEvent];
      switch (biEvent) {
        case 'PLATFORM_INSTALLATION':
          return reportProcess(evid, func, { appDefinitionId: appDefIds });
        case 'PROVISION_APPS':
          return reportProcess(evid, func, {
            appDefinitionId: appDefIds,
            name: func.name,
          });
        case 'UNIFIED_APP_INSTALL':
          const { appDefinitionId } =
            extra as ExtraDataForEvent<'UNIFIED_APP_INSTALL'>;

          return reportProcess(evid, func, {
            ...extra,
            origin: getOriginInfo(appDefinitionId),
            is_dependency: isDependencyApp(appDefinitionId),
            esi,
            dm_version: dmVersion,
          });
        case 'NEW_COMPONENTS_INSTALL':
          const { components_list } =
            extra as ExtraDataForEvent<'NEW_COMPONENTS_INSTALL'>;
          return reportProcess(evid, func, {
            ...extra,
            numberTotalComponents: components_list.length,
            components_list: JSON.stringify(components_list),
            name: func.name,
          });
        default:
          return reportProcess(evid, func, { ...extra, name: func.name });
      }
    },
    reportBI: <T extends keyof typeof BI_EVENT_IDS>(
      biEvent: T,
      extra: ExtraDataForEvent<T>,
    ) => {
      // Currently relevant only to NAVIGATE_TO_DEV_CENTER event.
      // If you add another event - please use similar conditions structure as in `withBI`
      // to provide the relevant extras which should not be given by the user
      const evid = BI_EVENT_IDS[biEvent];
      return reportEvent(evid, extra);
    },
  };
}
